import axios from 'axios';

// Utiliser l'URL de l'API depuis les variables d'environnement
// En production, REACT_APP_API_URL sera défini dans .env.production
// En développement, utiliser localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 
                     (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 secondes de timeout (augmenté pour les requêtes lentes)
});

/** En-têtes d’auth alignés sur l’interceptor (app_jwt puis fallback Google). Utiliser pour fetch/streaming (ex. Oliver). */
export function getAuthHeaders() {
  const headers = {};
  try {
    const authStorage = typeof window !== 'undefined' ? sessionStorage : null;
    if (authStorage) {
      const appJwt = authStorage.getItem('app_jwt');
      if (appJwt) {
        headers.Authorization = `Bearer ${appJwt}`;
        try {
          const payload = JSON.parse(atob(appJwt.split('.')[1]));
          const exp = payload.exp;
          const now = Math.floor(Date.now() / 1000);
          const isExpired = exp != null && exp < now;
        } catch (_) {}
      } else {
        const googleUser = authStorage.getItem('googleUser');
        if (googleUser) {
          const userData = JSON.parse(googleUser);
          if (userData.access_token) {
            headers.Authorization = `Bearer ${userData.access_token}`;
            console.warn('[Auth] getAuthHeaders: source=googleUser (token Google → l’API attend un JWT applicatif, 401 possible)');
          }
        }
        if (!headers.Authorization) {
          console.warn('[Auth] getAuthHeaders: aucun token en session');
        }
      }
    }
  } catch (_) {}
  return headers;
}

// Blocage des déconnexions globales (401 → auth:logout-required) pendant le login (évite 401 sur /auth/validate juste après connexion)
let isLoggingIn = false;
export const setLoggingIn = (value) => {
  isLoggingIn = value;
};

// Vérification de la session avant déconnexion : un 401 isolé peut être transitoire ; on confirme avec /auth/validate avant de déconnecter
let pending401Validation = null;
function validateSessionBeforeLogout(axiosInstance) {
  if (pending401Validation) return pending401Validation;
  pending401Validation = axiosInstance
    .get('/auth/validate', { timeout: 10000 })
    .then(() => {
      pending401Validation = null;
      return true;
    })
    .catch((err) => {
      pending401Validation = null;
      return err.response?.status === 401 ? false : true; // réseau/autre → ne pas déconnecter
    });
  return pending401Validation;
}

// ============================================
// PHASE 1: Système de déduplication et tracking
// ============================================

// Map pour stocker les requêtes en cours (déduplication)
const pendingRequests = new Map();

// Compteur pour le tracking des appels API
let apiCallCounter = {
  total: 0,
  byEndpoint: {},
  startTime: Date.now()
};

// Fonction pour générer une clé unique pour une requête
const getRequestKey = (config) => {
  const method = config.method?.toUpperCase() || 'GET';
  const url = config.url || '';
  const params = config.params ? JSON.stringify(config.params) : '';
  const data = config.data ? JSON.stringify(config.data) : '';
  return `${method}:${url}:${params}:${data}`;
};

// ============================================
// PHASE 2: Limite de concurrence (évite saturation connexions)
// ============================================
const MAX_CONCURRENT_REQUESTS = 6;
let activeRequests = 0;
const requestQueue = [];

const acquireSlot = () => {
  if (activeRequests < MAX_CONCURRENT_REQUESTS) {
    activeRequests++;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    requestQueue.push(resolve);
  });
};

const releaseSlot = () => {
  if (requestQueue.length > 0) {
    const next = requestQueue.shift();
    next();
  } else {
    activeRequests = Math.max(0, activeRequests - 1);
  }
};

const limitedRequest = (requestFn, config) => {
  return acquireSlot()
    .then(() => requestFn(config))
    .finally(releaseSlot);
};

// Fonction wrapper pour dédupliquer les requêtes (GET uniquement, idempotentes)
const dedupedRequest = (requestFn, config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get') {
    return requestFn(config);
  }
  const requestKey = getRequestKey(config);
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }
  const requestPromise = requestFn(config)
    .then((response) => {
      pendingRequests.delete(requestKey);
      return response;
    })
    .catch((error) => {
      pendingRequests.delete(requestKey);
      throw error;
    });
  pendingRequests.set(requestKey, requestPromise);
  return requestPromise;
};

// === Suivi de performance (pour Parametres > Logs) ===
const MAX_PERF_ENTRIES = 250;
const performanceLog = [];
export const getPerformanceLog = () => [...performanceLog];
export const clearPerformanceLog = () => {
  performanceLog.length = 0;
};
// ts en ISO pour que Parametres puisse parser avec new Date() puis afficher en Europe/Paris
const addPerformanceEntry = (url, method, durationMs, serverTimeMs, status) => {
  const entry = {
    type: 'api',
    ts: new Date().toISOString(),
    url: url || '?',
    method: method || 'GET',
    durationMs: Math.round(durationMs || 0),
    serverTimeMs: serverTimeMs ? parseFloat(serverTimeMs) : null,
    status: status || null,
    label: null
  };
  performanceLog.unshift(entry);
  if (performanceLog.length > MAX_PERF_ENTRIES) performanceLog.pop();
};

/** Enregistre une entrée de performance applicative (affichage page, calcul, etc.) */
export const logAppPerformance = (kind, name, durationMs, detail = null) => {
  const entry = {
    type: kind,
    ts: new Date().toISOString(),
    label: name,
    durationMs: Math.round(durationMs || 0),
    detail: detail || null,
    url: null,
    method: null,
    serverTimeMs: null,
    status: null
  };
  performanceLog.unshift(entry);
  if (performanceLog.length > MAX_PERF_ENTRIES) performanceLog.pop();
};

// Brancher déduplication + limite de concurrence sur api.request
const originalRequest = api.request.bind(api);
api.request = function (config) {
  return dedupedRequest(
    (cfg) => limitedRequest(originalRequest, cfg),
    config
  );
};

// Intercepteur de requête : attendre fin validation session (évite race 401 avatar/TWS), puis ajout du token + tracking
// Les requêtes TWS et health ne sont pas bloquées pour que l'app puisse se connecter à IB Gateway en arrière-plan même sans utilisateur connecté
const shouldSkipAuthValidationWait = (url) => {
  if (!url) return false;
  return (
    url.includes('/auth/validate') ||
    url.includes('/auth/login') ||
    url.includes('/auth/profile') ||
    url.includes('/tws/') ||
    url.includes('/health')
  );
};

api.interceptors.request.use(
  async (config) => {
    // Bloquer les requêtes (sauf auth, TWS, health) tant que la session est en cours de validation
    if (typeof window !== 'undefined' && window.authIsValidating === true && !shouldSkipAuthValidationWait(config.url)) {
      const maxWaitMs = 15000;
      const stepMs = 100;
      let waited = 0;
      while (window.authIsValidating === true && waited < maxWaitMs) {
        await new Promise((r) => setTimeout(r, stepMs));
        waited += stepMs;
      }
    }

    config.metadata = config.metadata || {};
    config.metadata.startTime = Date.now();
    // Même logique que getAuthHeaders() ci-dessous
    try {
      const authStorage = typeof window !== 'undefined' ? sessionStorage : null;
      if (authStorage) {
        const appJwt = authStorage.getItem('app_jwt');
        if (appJwt) {
          config.headers.Authorization = `Bearer ${appJwt}`;
        } else {
          const googleUser = authStorage.getItem('googleUser');
          if (googleUser) {
            const userData = JSON.parse(googleUser);
            if (userData.access_token) {
              config.headers.Authorization = `Bearer ${userData.access_token}`;
            }
          }
        }
      }
    } catch (error) {
      // Ignorer les erreurs de parsing (session invalide, etc.)
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ [API] Erreur lors de la récupération du token:', error);
      }
    }

    // Tracking
    const endpoint = config.url || 'unknown';
    apiCallCounter.total++;
    apiCallCounter.byEndpoint[endpoint] = (apiCallCounter.byEndpoint[endpoint] || 0) + 1;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Métriques API : logs uniquement en développement
const ENABLE_API_METRICS = process.env.NODE_ENV === 'development';
if (ENABLE_API_METRICS) {
  setInterval(() => {
    // Réinitialiser le compteur
    apiCallCounter = {
      total: 0,
      byEndpoint: {},
      startTime: Date.now()
    };
  }, 60000);
}

// Intercepteur de réponse : suivi performance + amélioration messages d'erreur
api.interceptors.response.use(
  (response) => {
    const start = response.config?.metadata?.startTime;
    if (typeof start === 'number') {
      const durationMs = Date.now() - start;
      const serverTime = response.headers?.['x-process-time'] || response.headers?.['X-Process-Time'];
      addPerformanceEntry(
        response.config?.url,
        response.config?.method,
        durationMs,
        serverTime,
        response.status
      );
    }
    return response;
  },
  (error) => {
    const start = error.config?.metadata?.startTime;
    if (typeof start === 'number') {
      const durationMs = Date.now() - start;
      addPerformanceEntry(
        error.config?.url,
        error.config?.method,
        durationMs,
        null,
        error.response?.status || 'ERR'
      );
    }
    // Ne pas logger les erreurs 404 pour user_avatar (c'est normal si l'utilisateur n'a pas d'avatar)
    const isAvatar404 = error.response?.status === 404 && 
                        error.config?.url?.includes('user_avatar');
    
    // Ne pas logger les erreurs 503 pour TWS non connecté (c'est normal)
    const isTWS503 = error.response?.status === 503 && 
                     error.config?.url && 
                     (error.config.url.includes('/tws/') || error.config.url.includes('/account') || error.config.url.includes('/positions')) &&
                     error.response?.data?.detail?.includes('TWS');
    
    // Ne pas logger les timeouts du Health Check (le backend peut être temporairement surchargé)
    const isHealthCheckTimeout = error.code === 'ECONNABORTED' && 
                                 error.config?.url?.includes('/health');
    
    // Ne pas logger le détail pour 401 (on logera uniquement le message de vérification session plus bas)
    const isAuth401 = error.response?.status === 401;
    
    if (!isAvatar404 && !isTWS503 && !isHealthCheckTimeout && !isAuth401) {
      // Log détaillé pour le diagnostic (seulement pour les vraies erreurs)
      const errorDetails = {
        code: error.code,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        fullURL: error.config?.baseURL + error.config?.url,
        request: error.request ? 'Request sent' : 'No request',
        response: error.response ? 'Response received' : 'No response',
        data: error.response?.data
      };
      console.error('🔴 [API Error]', errorDetails);
      // Afficher aussi les détails de manière plus lisible
      if (error.response?.data) {
        console.error('🔴 [API Error] Response data:', JSON.stringify(error.response.data, null, 2));
      }
    }
    // Améliorer le message d'erreur pour les timeouts
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      error.message = 'Le serveur ne répond pas dans les temps. Vérifiez que le backend est démarré.';
    } else if (error.response?.status === 401) {
      // Erreur 401 - Non autorisé (mot de passe incorrect ou session invalidée par connexion ailleurs)
      const detail = error.response?.data?.detail || error.response?.data?.message;
      const url = error.config?.url || '';
      const fullUrl = (error.config?.baseURL || '') + url;
      const isLoginRequest = url.includes('/auth/login');
      if (detail && (detail.includes('Email') || detail.includes('mot de passe') || detail.includes('password'))) {
        error.message = detail;
      } else if (isLoginRequest) {
        error.message = 'Email ou mot de passe incorrect';
      } else {
        error.message = detail || 'Authentification requise. Veuillez vous connecter.';
        // Ne pas déclencher la déconnexion globale pendant le login (évite 401 sur /auth/validate juste après connexion)
        if (isLoggingIn && typeof window !== 'undefined') {
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ [Auth] 401 reçu pendant le login, ignoré (pas de déconnexion)', { url: fullUrl });
          }
        } else if (typeof window !== 'undefined') {
          // Ne jamais dispatcher pour un 401 sur /auth/validate (évite double déconnexion)
          if (url.includes('/auth/validate')) {
            // pas de dispatch (géré par AuthContext)
          } else {
            // Vérifier la session avec /auth/validate avant de déconnecter (évite déconnexion sur 401 transitoire)
            validateSessionBeforeLogout(api).then((sessionStillValid) => {
              if (!sessionStillValid) {
                window.dispatchEvent(new CustomEvent('auth:logout-required', {
                  detail: { message: detail || error.message }
                }));
              }
            });
          }
        }
      }
    } else if (error.response?.status === 504) {
      error.message = 'Le serveur ne répond pas. Vérifiez que le backend est démarré.';
    } else if (error.response?.status === 503) {
      // Erreur 503 - Service Unavailable (TWS non connecté)
      const detail = error.response?.data?.detail || error.response?.data?.message;
      if (detail && (detail.includes('TWS') || detail.includes('IB Gateway') || detail.includes('non connecté'))) {
        // Ne pas logger les erreurs 503 pour TWS non connecté (c'est normal)
        // Mais garder le message d'erreur pour l'utilisateur si nécessaire
        error.message = 'TWS/IB Gateway non connecté. Veuillez vous connecter depuis Paramètres > API.';
      } else {
        error.message = detail || 'Service temporairement indisponible. Vérifiez que TWS/IB Gateway est lancé et connecté.';
      }
    } else if (error.response?.status === 500) {
      // Erreur 500 - Erreur serveur
      const detail = error.response?.data?.detail || error.response?.data?.message;
      if (detail) {
        error.message = detail;
      } else {
        error.message = 'Erreur serveur. Vérifiez les logs du backend.';
      }
    } else if (error.response?.status === 404) {
      // Erreur 404 - Route non trouvée
      // Récupérer l'URL complète qui a été réellement utilisée
      let fullUrl = 'route inconnue';
      if (error.config) {
        // Axios construit l'URL complète dans config.url après avoir combiné baseURL
        // Mais parfois c'est dans request.responseURL
        fullUrl = error.config.url || error.request?.responseURL || error.config.baseURL + error.config.url;
      } else if (error.request?.responseURL) {
        fullUrl = error.request.responseURL;
      }
      
      // Si l'URL ne commence pas par /api, l'ajouter (pour les routes relatives)
      if (!fullUrl.startsWith('/api') && !fullUrl.startsWith('http')) {
        const baseURL = error.config?.baseURL || '/api';
        fullUrl = baseURL + (fullUrl.startsWith('/') ? fullUrl : '/' + fullUrl);
      }
      
      const detail = error.response?.data?.detail || error.response?.data?.message;
      if (detail) {
        error.message = detail;
      } else {
        error.message = `Route non trouvée (404): ${fullUrl}. Vérifiez que le backend est démarré et que la route existe.`;
      }
    } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      // Erreur réseau (connexion refusée, pas de réponse, etc.)
      // Vérifier si c'est vraiment une erreur réseau ou un timeout
      if (error.code === 'ECONNREFUSED') {
        error.message = 'Impossible de se connecter au serveur. Vérifiez que le backend est démarré sur http://localhost:8000';
      } else {
        error.message = 'Erreur réseau. Vérifiez votre connexion et que le backend est démarré.';
      }
    } else if (!error.response && error.request) {
      // Requête envoyée mais pas de réponse - peut être un timeout ou une erreur réseau
      if (error.code === 'ECONNABORTED') {
        error.message = 'Le serveur met trop de temps à répondre. Vérifiez que le backend est démarré.';
      } else {
        error.message = 'Le serveur ne répond pas. Vérifiez que le backend est démarré sur http://localhost:8000';
      }
    }
    return Promise.reject(error);
  }
);

// Cache court pour tws/status (plusieurs composants l'appellent, requête ~1,3 s)
const TWS_STATUS_CACHE_MS = 20000; // 20 secondes
let twsStatusCache = { response: null, timestamp: 0 };
export const twsAPI = {
  connect: async () => {
    twsStatusCache = { response: null, timestamp: 0 }; // invalider après connexion
    return api.post('/tws/connect', {}, { timeout: 30000 });  // 30 secondes pour la connexion
  },
  disconnect: async () => {
    twsStatusCache = { response: null, timestamp: 0 }; // invalider après déconnexion
    return api.post('/tws/disconnect', {}, { timeout: 30000 });  // 30 secondes pour la déconnexion
  },
  getStatus: async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && twsStatusCache.response && (now - twsStatusCache.timestamp) < TWS_STATUS_CACHE_MS) {
      return Promise.resolve(twsStatusCache.response);
    }
    const response = await api.get('/tws/status', {
      timeout: 30000,
      params: forceRefresh ? { refresh: true } : undefined,
    });
    twsStatusCache = { response, timestamp: Date.now() };
    return response;
  },
  getPositions: async () => {
    return api.get('/tws/positions');  // ← api au lieu de apiClient
  },
  getOrders: async () => {
    return api.get('/tws/orders', { timeout: 30000 }); // 30 secondes pour les ordres
  },
  getAccount: async () => {
    return api.get('/tws/account');  // ← api au lieu de apiClient
  },
  getAccountSummary: async () => {
    return api.get('/tws/account-summary');  // ← api au lieu de apiClient
  },
  getDiagnostic: () => api.get('/tws/diagnostic', { timeout: 15000 }),
  getMarketData: async (symbol, secType, exchange, currency) => {
    return api.get(`/tws/market-data/${symbol}`, {  // ← api au lieu de apiClient
      params: { secType, exchange, currency }
    });
  },
};

export const organizationsAPI = {
  list: () => api.get('/organizations'),
  get: (id) => api.get(`/organizations/${id}`),
  create: (data) => api.post('/organizations', data),
  createWithAdmin: (data) => api.post('/organizations/with-admin', data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  updateStatus: (id, isActive) => api.patch(`/organizations/${id}/status?is_active=${isActive}`),
  delete: (id) => api.delete(`/organizations/${id}`),
  // Membres
  listMembers: (orgId) => api.get(`/organizations/${orgId}/members`),
  createMember: (orgId, userData, role = 'viewer') => api.post(`/organizations/${orgId}/members?role=${role}`, userData),
  updateMember: (orgId, memberId, data) => api.put(`/organizations/${orgId}/members/${memberId}`, data),
  deleteMember: (orgId, memberId) => api.delete(`/organizations/${orgId}/members/${memberId}`),
};

export const subscriptionsAPI = {
  list: () => api.get('/subscriptions'),
  listAll: () => api.get('/subscriptions/all'),
  get: (id) => api.get(`/subscriptions/${id}`),
  getByCode: (code) => api.get(`/subscriptions/code/${code}`),
  create: (data) => api.post('/subscriptions', data),
  update: (id, data) => api.put(`/subscriptions/${id}`, data),
  delete: (id) => api.delete(`/subscriptions/${id}`),
};

export const categoriesAPI = {
  list: () => api.get('/categories', { timeout: 30000 }), // 30 secondes pour le chargement des catégories
  get: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  toggleStatus: (id) => api.patch(`/categories/${id}/toggle-status`),
};

export const assetsAPI = {
  getBySymbol: (symbol) => api.get(`/assets/by-symbol/${encodeURIComponent(symbol)}`),
  findOrCreate: (symbol, name = null, yfinanceSymbol = null) => 
    api.post('/assets/find-or-create', { 
      symbol, 
      ...(name ? { name } : {}), 
      ...(yfinanceSymbol ? { yfinance_symbol: yfinanceSymbol } : {}) 
    }),
};

export const assetEnrichmentAPI = {
  getByActif: (actifId) => api.get(`/asset-enrichment/actif/${actifId}`),
  getBySymbol: (symbol) => api.get(`/asset-enrichment/by-symbol/${encodeURIComponent(symbol)}`),
  getBulkBySymbols: (symbols) => api.get(`/asset-enrichment/bulk-by-symbols?symbols=${symbols.map(s => encodeURIComponent(s)).join(',')}`),
  create: (data) => api.post('/asset-enrichment', data, { timeout: 30000 }), // 30 secondes pour la création
  update: (actifId, data) => api.put(`/asset-enrichment/actif/${actifId}`, data, { timeout: 30000 }), // 30 secondes pour la mise à jour
  updateBySymbol: (symbol, data) => api.put(`/asset-enrichment/by-symbol/${encodeURIComponent(symbol)}`, data, { timeout: 30000 }), // 30 secondes pour la mise à jour
  delete: (actifId) => api.delete(`/asset-enrichment/actif/${actifId}`),
};

export const settingsAPI = {
  // Pour les codes API, utiliser organization_id. Pour les autres paramètres, utiliser user_id
  list: (organizationId = 1, userId = null) => {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId.toString());
    if (userId) params.append('user_id', userId.toString());
    return api.get(`/settings?${params.toString()}`, { timeout: 30000 });
  },
  get: (paramName, organizationId = 1, userId = null) => {
    const params = new URLSearchParams();
    if (organizationId != null) {
      params.append('organization_id', organizationId.toString());
    }
    if (userId != null) {
      params.append('user_id', userId.toString());
    }
    // Timeout plus long pour les avatars (images volumineuses en base64)
    const timeout = paramName === 'user_avatar' ? 60000 : 30000; // 60 secondes pour les avatars, 30 pour le reste
    return api.get(`/settings/${encodeURIComponent(paramName)}?${params.toString()}`, { timeout });
  },
  create: (data) => api.post('/settings', data, { timeout: 30000 }),
  update: (paramName, paramValue, organizationId = 1, userId = null) => {
    const params = new URLSearchParams();
    if (organizationId != null) {
      params.append('organization_id', organizationId.toString());
    }
    if (userId != null) {
      params.append('user_id', userId.toString());
    }
    // Timeout plus long pour les avatars (images volumineuses en base64)
    const timeout = paramName === 'user_avatar' ? 60000 : 30000; // 60 secondes pour les avatars, 30 pour le reste
    return api.put(`/settings/${encodeURIComponent(paramName)}?${params.toString()}`, { param_value: paramValue }, { timeout });
  },
  delete: (paramName, organizationId = 1, userId = null) => {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId.toString());
    if (userId) params.append('user_id', userId.toString());
    return api.delete(`/settings/${encodeURIComponent(paramName)}?${params.toString()}`, { timeout: 30000 });
  },
  testKey: (provider, organizationId = 1) =>
    api.post(`/settings/test-keys/${provider}?organization_id=${organizationId}`, {}, { timeout: 15000 }),
};

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }, { timeout: 45000 }), // 45s (marge vs attente pool SQL + vérif mot de passe)
  getProfile: (email) => api.get(`/auth/profile/${encodeURIComponent(email)}`, { timeout: 30000 }),
  validate: () => api.get('/auth/validate', { timeout: 10000 }), // validation session (401 si connexion ailleurs)
  setPassword: (newPassword) => api.patch('/auth/me/password', { new_password: newPassword }, { timeout: 10000 }),
};

export const macroAPI = {
  getIndices: () => api.get('/macro/indices', { timeout: 60000 }), // 60 secondes pour le chargement des indices
  getCommodities: () => api.get('/macro/commodities', { timeout: 60000 }), // 60 secondes pour le chargement des matières premières
};

export const analyseAPI = {
  uploadSingle: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/analyse/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes pour l'analyse IA
    });
  },
  uploadMultiple: (files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return api.post('/analyse/upload-multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes pour plusieurs fichiers
    });
  },
  list: (limit = 10) => api.get(`/analyse/list?limit=${limit}`, { timeout: 60000 }), // 60 secondes pour le chargement
  get: (analysisId) => api.get(`/analyse/${analysisId}`, { timeout: 30000 }),
  getMetaAnalysis: (forceRegenerate = false) => {
    const params = forceRegenerate ? '?force_regenerate=true' : '';
    return api.get(`/analyse/meta/last-30-days${params}`, { timeout: 120000 }); // 2 minutes pour la méta-analyse
  },
  /** Export Markdown BEAM v2 : POST avec payload (accountSummary, positions, feed…) — blob + Content-Disposition */
  exportMarkdown: (payload) =>
    api.post('/analyse/export-markdown', payload, {
      responseType: 'blob',
      timeout: 300000,
    }),
};

export const xFeedAPI = {
  getTweets: (limit = 20) => api.get(`/x-feed/tweets?limit=${limit}`, { timeout: 30000 }), // 30 secondes pour récupérer les tweets
  getDiagnostic: () => api.get('/x-feed/diagnostic', { timeout: 30000 }), // Diagnostic de la configuration
};

export const ibGatewayConfigAPI = {
  get: (organizationId = 1) => api.get(`/ib-gateway-config/?organization_id=${organizationId}`, { timeout: 30000 }),
  create: (data, organizationId = 1) => api.post(`/ib-gateway-config/?organization_id=${organizationId}`, data, { timeout: 30000 }),
  update: (data, organizationId = 1) => api.put(`/ib-gateway-config/?organization_id=${organizationId}`, data, { timeout: 30000 }),
  delete: (organizationId = 1) => api.delete(`/ib-gateway-config/?organization_id=${organizationId}`, { timeout: 30000 }),
};

export const healthAPI = {
  getHealth: () => {
    // L'endpoint est maintenant sous /api/health pour être accessible via Nginx
    // Timeout augmenté à 10 secondes pour éviter les timeouts lors de surcharge
    return api.get('/health', { timeout: 10000 });
  },
  restartBackend: () => {
    // L'endpoint /restart est dans /api
    return api.post('/health/restart', {}, { timeout: 10000 });
  },
};

export const portfolioAPI = {
  /** Positions depuis le cache Redis (Oliver, TTL 5 min). Format identique à TWS. Secours quand TWS indisponible. */
  getCachedPositions: () => api.get('/portfolio/cached-positions', { timeout: 15000 }),
  // Valorisations — Valeur de la Position (Portefeuille, alimenté par le service 22h30)
  getValuations: (startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return api.get(`/portfolio/valuations?${params.toString()}`, { timeout: 30000 });
  },
  getLatestValuation: () => api.get('/portfolio/valuations/latest', { timeout: 30000 }),
  getCurrentValuation: () => api.get('/portfolio/valuations/current', { timeout: 30000 }),
  saveCurrentValuation: (commentaire = null) => api.post('/portfolio/valuations/save-current', { commentaire }, { timeout: 30000 }),
  createValuation: (data) => api.post('/portfolio/valuations', data, { timeout: 30000 }),
  updateValuation: (id, data) => api.put(`/portfolio/valuations/${id}`, data, { timeout: 30000 }),
  deleteValuation: (id) => api.delete(`/portfolio/valuations/${id}`, { timeout: 30000 }),

  // Valeur Nette (Compte, alimenté par le service 22h30)
  getValeurNette: (startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return api.get(`/portfolio/valeur-nette?${params.toString()}`, { timeout: 30000 });
  },
  createValeurNette: (data) => api.post('/portfolio/valeur-nette', data, { timeout: 30000 }),
  updateValeurNette: (id, data) => api.put(`/portfolio/valeur-nette/${id}`, data, { timeout: 30000 }),
  deleteValeurNette: (id) => api.delete(`/portfolio/valeur-nette/${id}`, { timeout: 30000 }),
  importValeurNetteCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/portfolio/valeur-nette/import-csv', formData, {
      timeout: 60000,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Snapshots de valeur par position (Valorisation Portefeuille, reprise à 22h00)
  getPositionSnapshots: (startDate = null, endDate = null, symbol = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (symbol) params.append('symbol', symbol);
    return api.get(`/portfolio/position-snapshots?${params.toString()}`, { timeout: 30000 });
  },
  createPositionSnapshot: (data) => api.post('/portfolio/position-snapshots', data, { timeout: 30000 }),
  updatePositionSnapshot: (id, data) => api.put(`/portfolio/position-snapshots/${id}`, data, { timeout: 30000 }),
  deletePositionSnapshot: (id) => api.delete(`/portfolio/position-snapshots/${id}`, { timeout: 30000 }),

  // Apports/Retraits
  getApports: (startDate = null, endDate = null, investisseur = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (investisseur) params.append('investisseur', investisseur);
    return api.get(`/portfolio/apports?${params.toString()}`, { timeout: 30000 });
  },
  createApport: (data) => api.post('/portfolio/apports', data, { timeout: 30000 }),
  updateApport: (id, data) => api.put(`/portfolio/apports/${id}`, data, { timeout: 30000 }),
  deleteApport: (id) => api.delete(`/portfolio/apports/${id}`, { timeout: 30000 }),
  
  // Investisseurs
  getInvestisseurs: (actifOnly = true) => api.get(`/portfolio/investisseurs?actif_only=${actifOnly}`, { timeout: 30000 }),
  
  // Health check - endpoint à la racine, pas dans /api
  getHealth: () => {
    // L'endpoint /health est à la racine, pas dans /api
    const baseURL = process.env.REACT_APP_API_URL || 
                   (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');
    // Timeout augmenté à 10 secondes pour éviter les timeouts lors de surcharge
    return axios.get(`${baseURL}/health`, { timeout: 10000 });
  },
  createInvestisseur: (data) => api.post('/portfolio/investisseurs', data, { timeout: 30000 }),
  updateInvestisseur: (id, data) => api.put(`/portfolio/investisseurs/${id}`, data, { timeout: 30000 }),
  deleteInvestisseur: (id) => api.delete(`/portfolio/investisseurs/${id}`, { timeout: 30000 }),
  
  // Commentaires
  getCommentaires: (startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return api.get(`/portfolio/commentaires?${params.toString()}`, { timeout: 30000 });
  },
  createCommentaire: (data) => api.post('/portfolio/commentaires', data, { timeout: 30000 }),
  updateCommentaire: (id, data) => api.put(`/portfolio/commentaires/${id}`, data, { timeout: 30000 }),
  deleteCommentaire: (id) => api.delete(`/portfolio/commentaires/${id}`, { timeout: 30000 }),
  
  // Calculs de performance
  getPLData: (startDate, endDate, organizationId = 1, userId = 1) => {
    const params = new URLSearchParams();
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    params.append('organization_id', organizationId.toString());
    params.append('user_id', userId.toString());
    return api.get(`/portfolio/performance/pl?${params.toString()}`, { timeout: 60000 });
  },
  getValuationEvolution: (startDate, endDate, organizationId = 1, userId = 1, useValeurNette = false) => {
    const params = new URLSearchParams();
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    params.append('organization_id', organizationId.toString());
    params.append('user_id', userId.toString());
    if (useValeurNette) params.append('use_valeur_nette', 'true');
    return api.get(`/portfolio/performance/valuation-evolution?${params.toString()}`, { timeout: 60000 });
  },
  getMaxGainsLosses: (startDate, endDate, organizationId = 1, userId = 1) => {
    const params = new URLSearchParams();
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    params.append('organization_id', organizationId.toString());
    params.append('user_id', userId.toString());
    return api.get(`/portfolio/performance/max-gains-losses?${params.toString()}`, { timeout: 60000 });
  },
  getInvestisseursValues: (organizationId = 1, userId = 1) => {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId.toString());
    params.append('user_id', userId.toString());
    return api.get(`/portfolio/performance/investisseurs-values?${params.toString()}`, { timeout: 60000 });
  },
};

export const adminAPI = {
  getLogs: (service = 'backend', lines = 200) =>
    api.get('/admin/logs', { params: { service, lines }, timeout: 15000 }),
};

export default api;